import * as React from "react";

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/primitives/dialog";
import { useIsMobile } from "@/shared/hooks/use-mobile";

import { ColocaDrawer } from "./ColocaDrawer";

export interface ColocaDialogProps {
  /**
   * Element that opens the dialog — typically a `<ColocaButton>`. The dialog wires up the click handler
   * automatically; do not add your own `onClick` to the trigger. Omit when the dialog is opened
   * programmatically via `open` / `onOpenChange`.
   */
  trigger?: React.ReactElement;
  /** Short title displayed at the top of the dialog. Keep to one line. */
  title: React.ReactNode;
  /** Supporting text shown below the title. Use for context or instructions. */
  description?: React.ReactNode;
  /**
   * Fixed strip between the header and the scrolling body — a wizard's step indicator, a filter row, a
   * persistent notice. Use it for anything that must stay visible while the content moves under it; anything
   * else belongs in `children`.
   */
  toolbar?: React.ReactNode;
  /** Main body content — forms, lists, detail panels, or any other React nodes. */
  children?: React.ReactNode;
  /**
   * Action buttons rendered in the dialog footer. Wrap primary actions in `<ColocaButton variant="primary">`.
   * Use `<DialogClose>` from the primitives to wire up cancel or close buttons inside the footer.
   */
  footer?: React.ReactNode;
  /**
   * Dialog width. - `"sm"` — `max-w-sm` on desktop. Use for short confirmations or single-field inputs. -
   * `"default"` — `max-w-lg` on desktop. Suitable for most forms and detail panels. - `"lg"` — `max-w-2xl` on
   * desktop. Use for multi-step forms or content-heavy dialogs. - `"xl"` — `max-w-4xl` on desktop. Reserved
   * for dense flows that need a side-by-side layout (e.g. form + live preview).
   */
  size?: "sm" | "default" | "lg" | "xl";
  /**
   * Whether to render the × close button in the top-right corner. Defaults to `true`. Set to `false` when the
   * only dismissal path should be through an explicit footer action (e.g. mandatory acknowledgment dialogs).
   */
  showCloseButton?: boolean;
  /** Controlled open state. Pair with `onOpenChange` to manage the dialog from outside. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * On viewports below the `md` breakpoint (768px), render as a fullscreen side panel instead of a centered
   * modal. Recommended for long forms or multi-step wizards that don't fit comfortably in a small modal on
   * mobile. Defaults to `false` (always render as a centered modal).
   */
  mobileFullscreen?: boolean;
  /**
   * What receives focus on open (desktop modal only). Defaults to the first tabbable element, which scrolls a
   * long body to wherever that element sits — point it at the top of the content when the body must be read
   * from the start.
   */
  initialFocus?: React.ComponentProps<typeof DialogContent>["initialFocus"];
}

/**
 * General-purpose modal dialog for secondary content, forms, and confirmations.
 *
 * Use `ColocaDialog` when users need to complete a focused task without leaving the current page — creating a
 * record, editing details, or reviewing information. For irreversible destructive actions use
 * `ColocaAlertDialog` instead.
 *
 * Pass any content via `children` and provide action buttons through `footer`. Use `<DialogClose>` from the
 * dialog primitives to wire up cancel or close buttons inside the footer.
 */
export function ColocaDialog({
  trigger,
  title,
  description,
  toolbar,
  children,
  footer,
  size = "default",
  showCloseButton = true,
  open,
  onOpenChange,
  mobileFullscreen = false,
  initialFocus,
}: ColocaDialogProps) {
  const isMobile = useIsMobile();

  if (mobileFullscreen && isMobile) {
    return (
      <ColocaDrawer
        trigger={trigger}
        title={title}
        description={description}
        // Its own slot, not the top of the body: `toolbar` promises to stay put while the content scrolls,
        // and on this path the body is the scroller. Prepending it to `children` also added a second root
        // element to the body, which silently flipped the `pinLastChild` heuristic below.
        toolbar={toolbar}
        footer={footer}
        showCloseButton={showCloseButton}
        open={open}
        onOpenChange={onOpenChange}
        size="lg"
        // The drawer pins the last body child to the bottom edge when the body has a single root, a helper for
        // wizards that embed their own action row. With a real `footer` that pin can only misfire — it would
        // drag a content block down instead. See `ColocaDrawerProps.pinLastChild`.
        pinLastChild={!footer}
      >
        {children}
      </ColocaDrawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger nativeButton render={trigger} />}
      <DialogContent size={size} showCloseButton={showCloseButton} initialFocus={initialFocus}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {toolbar && <div className="shrink-0">{toolbar}</div>}
        {/* The body is the only region that scrolls; everything above and below it stays put. */}
        <DialogBody>{children}</DialogBody>
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

export { DialogClose as ColocaDialogClose };
